import { motion } from "motion/react";
import { Code2, Bot, Cpu, Globe, Rocket, Terminal, Layers, Palette, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { Link } from "react-router-dom";
import { useState } from "react";

export function Programs() {
  const categories = [
    { id: 'coding', label: 'Coding', icon: <Code2 className="w-5 h-5" />, color: 'bg-[#5B9BD5]' },
    { id: 'robotics', label: 'Robotics', icon: <Bot className="w-5 h-5" />, color: 'bg-[#00D4D4]' },
    { id: 'innovation', label: 'Innovation', icon: <Rocket className="w-5 h-5" />, color: 'bg-[#FFD700]' },
    { id: 'web', label: 'Web Development', icon: <Globe className="w-5 h-5" />, color: 'bg-[#5B9BD5]' },
    { id: 'mobile', label: 'Mobile Apps', icon: <Layers className="w-5 h-5" />, color: 'bg-[#A855F7]' },
    { id: 'game', label: 'Game Development', icon: <Rocket className="w-5 h-5" />, color: 'bg-[#F78C40]' }
  ];

  const programs = [
    {
      category: 'coding',
      title: 'Scratch Coding Fundamentals',
      subtopic: 'Programming with Scratch',
       slug: 'programming with scratch',
      age: '5-7',
      level: 'Beginner',
      description: 'Learners use Scratch code blocks to understand sequencing, loops, logic, animation and simple game design in a beginner-friendly and creative environment.',
      outcomes: ['Computational Thinking', 'Storytelling Logic', 'Creative Problem Solving'],
      image: 'scratchpen.png' 
    },
    {
      category: 'coding',
      title: 'Python Pioneers',
      subtopic: 'Python and JavaScript programming ',
      slug: 'python-and-javascript-programming',
      age: '11-14',
      level: 'Intermediate',
      description: 'Transitioning from blocks to text. Students master Python, the world’s most versatile language, building applications from data analyzers to desktop games.',
      outcomes: ['Code Structuring', 'Error Handling', 'Application Development Basics'],      
        image: 'Whisk_PY.png'
    },
    {
      category: "robotics",
      title: "LEGO Mindstorms & Arduino Robotics",
      slug: 'lego-arduino-robotics',  
      age: "9+",
      level: "Beginner-Intermediate",
      description:
        "Students build and program real robots using LEGO and Arduino-based robotics using accessible kits.",
      outcomes: ["Robot Building", "Sensor Programming", "Team-Based Engineering"],
      image: "arduino_pieces2.jpeg" 
      },

      {
      category: 'innovation',
      title: 'AI & Data Science',
      slug: 'ai-data-science',
      age: '14+',
      level: 'Advanced',
      description: 'Learners explore how AI tools can be used in real-world applications, combining them with coding and robotics to create smart and innovative solutions.',      
      outcomes: ['Exploring AI Tools', 'Creative Building', 'Innovation Skills'],
      image: 'Whisk_scratch_AI.jpeg'
      },
    {
      category: 'web',
      title: 'Web Development',
      subtopic: 'Web Apps and websites using CSS and HTML',
      slug: 'web-development',
      age: '9+',
      level: 'Intermediate',
      description: '          In this course, learners are introduced to the fundamentals of web development using HTML and CSS. They learn how to create, structure and style web pages while building their own simple websites through practical, hands-on projects.',      
      outcomes: ['Building Web Pages', 'HTML Structure', 'CSS Styling', 'Creative Design'],
      image: 'Whisk_scratch_AI.jpeg'
    },
  {
    category: "schools",
    title: "Teacher Training & Equipment Support",
    slug: 'teacher-training-support',
    age: "Teachers",
    level: "School Support",
    description:
      "Astra trains teachers on instructional strategies, robotics kit setup, classroom integration and curriculum support, while also assisting schools to select suitable durable robotics kits.",
    outcomes: ["Teacher Capacity Building", "Equipment Setup", "Curriculum Integration"],
    image: "Whisk_teachersupport.png"
  }];

    const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#2E5C8A] to-[#5B9BD5] py-24 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full opacity-10">
          <div className="grid grid-cols-6 gap-4 rotate-12 -translate-y-20">
            {Array.from({ length: 24 }).map((_, i) => (
              <div key={i} className="aspect-square bg-white rounded-2xl" />
            ))}
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl mb-4 leading-[1.1] tracking-tight">Our Programs</h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              From the first line of code to building intelligent autonomous robots, 
              we have a pathway for every aspiring innovator.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pathways Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 justify-center mb-16 relative">
  {categories.map((cat) => (
    <div
      key={cat.id}
      className="relative"
      onMouseEnter={() => setActiveCategory(cat.id)}
      onMouseLeave={() => setTimeout(() => setActiveCategory(null), 150)}
    >
      <button className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all border border-gray-100 font-bold text-[#0A1A2F]">
        <div className={`${cat.color} p-2 rounded-lg text-white`}>
          {cat.icon}
        </div>
        {cat.label}
      </button>

      {/* Coding Dropdown */}
      {cat.id === "coding" && activeCategory === "coding" && (
        <div className="absolute left-1/2 -translate-x-1/3 mt-3 w-72 bg-white shadow-2xl rounded-2xl border border-gray-100 p-4 z-50">
          <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
            Programming Courses
          </p>

          {programs
            .filter((p) => p.category === "coding")
            .map((program, index) => (
              <Link
                key={index}
                to={`/programs/${program.slug}`}
                className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 cursor-pointer transition"
              >
                <div>
                  <p className="font-semibold text-[#0A1A2F]">
                    {program.subtopic}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            ))}
        </div>
      )}

      {/* Web Development Dropdown */}
      {cat.id === "web" && activeCategory === "web" && (
        <div className="absolute left-1/2 -translate-x-1/3 mt-3 w-72 bg-white shadow-2xl rounded-2xl border border-gray-100 p-4 z-50">
          <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
            Web Courses
          </p>

          {programs
            .filter((p) => p.category === "web")
            .map((program, index) => (
              <Link
                key={index}
                to={`/programs/${program.slug}`}
                className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 cursor-pointer transition"
              >
                <div>
                  <p className="font-semibold text-[#0A1A2F]">
                    {program.subtopic}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            ))}
        </div>
      )}
    </div>
  ))}
</div>

          <div className="space-y-12">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row gap-12 items-center ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className="w-full lg:w-1/2">
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#2E5C8A] to-[#00FFFF] rounded-[48px] opacity-20 blur-2xl group-hover:opacity-30 transition-opacity" />
                    <div className="relative aspect-video rounded-[40px] overflow-hidden border-8 border-white shadow-2xl">
                      <ImageWithFallback src={program.image} alt={program.title} className="w-full h-full object-cover" />
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-1/2">
                  <div className="inline-flex gap-4 mb-6">
                    <span className="bg-[#E8F4FB] text-[#2E5C8A] px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider">
                      Ages {program.age}
                    </span>
                    <span className="bg-[#FFF9C4] text-[#FBC02D] px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider">
                      {program.level}
                    </span>
                  </div>
                  <h2 className="text-4xl font-extrabold text-[#0A1A2F] mb-6 tracking-tight">{program.title}</h2>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">{program.description}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-10">
                    {program.outcomes.map((outcome, i) => (
                      <div key={i} className="flex items-center gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                        <div className="w-2 h-2 bg-[#00FFFF] rounded-full" />
                        <span className="font-bold text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>

                  <Button className="h-14 px-10 rounded-full bg-[#2E5C8A] hover:bg-[#5B9BD5] text-white font-bold text-lg shadow-xl shadow-blue-100">
                    Enroll in this Course
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1A2F] mb-6">How We Teach</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Our 4-step pedagogical framework ensures students don't just consume technology, but master it.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Conceptual Explore', desc: 'Understanding the logic and science behind the technology through unplugged activities.' },
              { step: '02', title: 'Guided Build', desc: 'Step-by-step instruction to construct the core project of the lesson.' },
              { step: '03', title: 'Creative Modify', desc: 'Encouraging students to change parameters and add their own unique features.' },
              { step: '04', title: 'Grand Challenge', desc: 'Applying everything learned to solve a new, unguided problem.' },
            ].map((step, i) => (
              <div key={i} className="bg-white p-8 rounded-[32px] shadow-lg border border-gray-100 relative">
                <div className="text-6xl font-black text-[#5B9BD5]/10 absolute top-4 right-4">{step.step}</div>
                <h4 className="text-xl font-bold text-[#0A1A2F] mb-4 relative z-10">{step.title}</h4>
                <p className="text-gray-600 leading-relaxed relative z-10">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
